// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, Grid } from '@mui/material'
import { FC, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

// component import
import { Alert } from '../../../common/Alert'
import { CustomController } from '../../../common/CustomController'
import { CustomModal } from '../../../common/CustomModal'
import HourlyCasesController from './HourlyCasesController'

// others
import { useFetchFundRequestLazyQuery, useUpdateFundRequestMutation } from '../../../../../generated'
import { BAD_USER_INPUT, CANCEL, GRAPHQL_QUERY_POLICY, SOMETHING_WENT_WRONG } from '../../../../constants'
import { FundRequestFormValues } from '../../../../interfaceTypes/forms'
import { fundRequestValidationSchema } from '../../../../validationSchema'

interface EditFundRequestModalProps {
  billId: number
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export const EditFundRequestModal: FC<EditFundRequestModalProps> = ({
  billId,
  isOpen,
  onClose,
  onSuccess
}): JSX.Element => {
  const [fetchFundRequest, { loading: dataLoading }] = useFetchFundRequestLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      if (data) {
        const {
          fetchFundRequest: { fundRequest }
        } = data

        if (fundRequest) {
          reset({
            case: fundRequest.case ? { id: fundRequest.case?.id, title: fundRequest.case?.title } : null,
            amount: fundRequest.amount,
            description: fundRequest.description || undefined
          })
        }
      }
    }
  })

  const [updateFundRequestMutation, { loading }] = useUpdateFundRequestMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { code } = extensions
            if (code === BAD_USER_INPUT) {
              Alert.error(error.message)
            } else {
              Alert.error(SOMETHING_WENT_WRONG)
            }
          }

          return null
        })
      } else {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },
    onCompleted(data) {
      if (data) {
        const {
          updateFundRequest: { response, fundRequest }
        } = data

        if (response) {
          const { status } = response

          if (status === 200 && fundRequest) {
            Alert.success('Fund request updated successfully')
            onClose()
            onSuccess()
          }
        }
      }
    }
  })

  const methods = useForm<FundRequestFormValues>({
    mode: 'all',
    disabled: dataLoading,
    resolver: yupResolver(fundRequestValidationSchema),
    defaultValues: {
      case: null,
      amount: '',
      description: ''
    }
  })

  const { handleSubmit, reset } = methods

  const onSubmit: SubmitHandler<FundRequestFormValues> = async data => {
    const { case: litigation, amount, description } = data
    await updateFundRequestMutation({
      variables: {
        updateFundRequestInput: {
          id: billId,
          caseId: litigation?.id,
          amount: amount || 0,
          description
        }
      }
    })
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      fetchFundRequest({ variables: { billId } })
    }
  }, [isOpen, billId, fetchFundRequest])

  return (
    <CustomModal title="Edit Fund Request" isOpen={isOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HourlyCasesController />
            </Grid>

            <Grid item xs={12}>
              <CustomController controllerName="amount" controllerLabel="Amount" fieldType="number" sx={{ my: 0 }} />
            </Grid>

            <Grid item xs={12}>
              <CustomController
                controllerName="description"
                controllerLabel="Description"
                fieldType="textarea"
                isMultiLine
                rowsLength={4}
                sx={{ my: 0 }}
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose}>{CANCEL}</Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || dataLoading}
              endIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              Update
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default EditFundRequestModal
